/**
 * Sidebar tab content for the Details tab: renders `<PropertyDetailsSidebar>`
 * with the hydrated feature collection, current overlay configs, and the
 * batch-edit / archive / tag callbacks. Extracted from MPV's
 * `renderSidebarViews` so each sidebar tab content lives in its own file.
 */

import * as I from 'immutable';
import React from 'react';

import PropertyDetailsSidebar from 'app/components/PropertyDetailsSidebar/PropertyDetailsSidebar';
import {OverlayConfig} from 'app/modules/Remote/api';
import {ApiFeature} from 'app/modules/Remote/Feature';
import {
  ApiFeatureCollection,
  HydratedFeatureCollection,
} from 'app/modules/Remote/FeatureCollection';
import {ApiOrganization, ApiOrganizationUser} from 'app/modules/Remote/Organization';
import {ApiProject} from 'app/modules/Remote/Project';
import {LoggedInUserActions} from 'app/providers/AuthProvider';
import {FeaturesActions} from 'app/providers/FeaturesProvider';
import * as featureCollectionUtils from 'app/utils/featureCollectionUtils';
import * as mapUtils from 'app/utils/mapUtils';

interface Props {
  selectedFeature: I.ImmutableOf<ApiFeature>;
  hydratedFC: HydratedFeatureCollection;
  overlayFeatureCollections: I.ImmutableOf<ApiFeatureCollection[]>;
  visibleOverlayConfigs: OverlayConfig[];
  organization: I.ImmutableOf<ApiOrganization>;
  organizationUsers: ApiOrganizationUser[];
  profile: I.ImmutableOf<ApiOrganizationUser>;
  selectedProject: I.ImmutableOf<ApiProject>;
  firebaseToken: string;
  imageRefs: mapUtils.MapImageRefs;
  activeLayerKey: string;
  batchPatchFeature: FeaturesActions['batchPatchFeature'];
  batchArchiveFeature: FeaturesActions['batchArchiveFeatures'];
  setTags: LoggedInUserActions['setOrganizationTags'];
}

export default function DetailsPanel({
  selectedFeature,
  hydratedFC,
  overlayFeatureCollections,
  visibleOverlayConfigs,
  organization,
  organizationUsers,
  profile,
  selectedProject,
  firebaseToken,
  imageRefs,
  activeLayerKey,
  batchPatchFeature,
  batchArchiveFeature,
  setTags,
}: Props) {
  return (
    <PropertyDetailsSidebar
      firebaseToken={firebaseToken}
      featureCollection={featureCollectionUtils.hydratedFeatureCollectionAsApiFeatureCollection(
        hydratedFC
      )}
      allFeatures={hydratedFC.get('features')}
      overlayFeatureCollections={overlayFeatureCollections}
      visibleOverlayConfigs={visibleOverlayConfigs}
      selectedFeatureId={selectedFeature.get('id')}
      batchPatchFeature={batchPatchFeature}
      batchArchiveFeature={batchArchiveFeature}
      setTags={setTags}
      role={profile.get('role')}
      organization={organization}
      organizationUsers={organizationUsers}
      activeLayerKey={activeLayerKey}
      // We're using some hard-coded values here for width. The width of the TabbedSidebar
      // is 36rem, and the PropertyDetailsSidebar specifies 1rem of padding. Any elements
      // occupying 100% of the padded area would then be 36rem - (2 * 1rem) = 34rem or
      // 340px.
      layerLegendWidth={340}
      imageRefs={imageRefs}
      project={selectedProject}
    />
  );
}
